import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import { useAuth } from '../context/AuthContext';
import api from '../utils/api';
import { toast } from 'react-toastify';
import { FaBox, FaClock, FaTruck, FaCheckCircle, FaTimesCircle, FaFileInvoice } from 'react-icons/fa';
import './Orders.css';

const Orders = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [orders, setOrders] = useState([]);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState('all');

  useEffect(() => {
    fetchOrders();
  }, []);

  const fetchOrders = async () => {
    try {
      const response = await api.get('/orders');
      setOrders(response.data.orders || []);
    } catch (error) {
      console.error('Failed to fetch orders:', error);
      toast.error('Failed to load orders');
    } finally {
      setLoading(false);
    }
  };

  const handleStatusUpdate = async (orderId, newStatus) => {
    try {
      await api.put(`/orders/${orderId}/status`, { 
        status: newStatus,
        note: `Status updated to ${newStatus}`
      });
      toast.success('Order status updated!');
      fetchOrders();
    } catch (error) {
      console.error('Status update error:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const handleCompletePayment = async (orderId) => {
    if (!window.confirm('Complete payment for this order? (Demo mode - no actual payment required)')) {
      return;
    }
    
    try {
      const order = orders.find(o => o._id === orderId);
      const response = await api.post('/payments/complete', {
        orderId: orderId,
        paymentMethod: order?.payment?.method || 'upi'
      });
      
      toast.success('Payment completed successfully! Invoice generated.');
      
      // Refresh orders to show updated status
      fetchOrders();
      
      // Navigate to invoice if generated
      if (response.data.invoiceId) {
        setTimeout(() => {
          navigate(`/invoices/${response.data.invoiceId}`);
        }, 1500);
      }
    } catch (error) {
      console.error('Payment error:', error);
      toast.error(error.response?.data?.message || 'Failed to complete payment');
    }
  };

  const handleCancelOrder = async (orderId) => {
    if (!window.confirm('Are you sure you want to cancel this order?')) {
      return;
    }
    
    try {
      await api.put(`/orders/${orderId}/cancel`);
      toast.success('Order cancelled successfully');
      fetchOrders();
    } catch (error) {
      console.error('Cancel order error:', error);
      toast.error(error.response?.data?.message || 'Failed to cancel order');
    }
  };

  const handleMarkDelivered = async (orderId) => {
    try {
      await api.put(`/orders/${orderId}/status`, { 
        status: 'delivered',
        note: 'Order marked as delivered'
      });
      toast.success('Order marked as delivered!');
      fetchOrders();
    } catch (error) {
      console.error('Mark delivered error:', error);
      toast.error(error.response?.data?.message || 'Failed to update status');
    }
  };

  const getStatusIcon = (status) => {
    switch (status) {
      case 'pending_payment':
      case 'interest':
        return <FaClock style={{ color: '#f59e0b' }} />;
      case 'paid':
      case 'confirmed':
      case 'processing':
        return <FaBox style={{ color: '#3b82f6' }} />;
      case 'shipped':
      case 'in_transit':
        return <FaTruck style={{ color: '#8b5cf6' }} />;
      case 'delivered':
      case 'completed':
        return <FaCheckCircle style={{ color: '#10b981' }} />;
      case 'cancelled':
      case 'refunded':
        return <FaTimesCircle style={{ color: '#ef4444' }} />;
      default:
        return <FaClock style={{ color: '#6b7280' }} />;
    }
  };

  const getStatusBadgeClass = (status) => {
    if (['delivered', 'completed'].includes(status)) return 'badge-success';
    if (['shipped', 'in_transit'].includes(status)) return 'badge-info';
    if (['cancelled', 'refunded'].includes(status)) return 'badge-danger';
    return 'badge-warning';
  };

  const filteredOrders = filter === 'all' 
    ? orders 
    : filter === 'pending_payment'
    ? orders.filter(order => order.status === 'pending_payment')
    : filter === 'confirmed'
    ? orders.filter(order => ['paid', 'confirmed', 'processing'].includes(order.status))
    : filter === 'shipped'
    ? orders.filter(order => ['shipped', 'in_transit'].includes(order.status))
    : filter === 'completed'
    ? orders.filter(order => ['delivered', 'completed'].includes(order.status))
    : orders.filter(order => order.status === filter);

  if (loading) return <Layout><div className="loading">Loading orders...</div></Layout>;

  const isSeller = user?.userType === 'seller';

  return (
    <Layout>
      <div className="orders-page">
        <div className="orders-header">
          <div>
            <h1>Orders</h1>
            <p>{isSeller ? 'Manage your customer orders' : 'Track your purchases'}</p>
          </div>
        </div>

        <div className="orders-stats card">
          <div className="stat-item" onClick={() => setFilter('all')}>
            <div className="stat-number">{orders.length}</div>
            <div className="stat-label">Total Orders</div>
          </div>
          <div className="stat-item" onClick={() => setFilter('pending_payment')}>
            <div className="stat-number">{orders.filter(o => o.status === 'pending_payment').length}</div>
            <div className="stat-label">Pending Payment</div>
          </div>
          <div className="stat-item" onClick={() => setFilter('confirmed')}>
            <div className="stat-number">{orders.filter(o => ['paid', 'confirmed', 'processing'].includes(o.status)).length}</div>
            <div className="stat-label">Confirmed</div>
          </div>
          <div className="stat-item" onClick={() => setFilter('shipped')}>
            <div className="stat-number">{orders.filter(o => ['shipped', 'in_transit'].includes(o.status)).length}</div>
            <div className="stat-label">In Transit</div>
          </div>
          <div className="stat-item" onClick={() => setFilter('completed')}>
            <div className="stat-number">{orders.filter(o => ['delivered', 'completed'].includes(o.status)).length}</div>
            <div className="stat-label">Completed</div>
          </div>
        </div>

        {filteredOrders.length === 0 ? (
          <div className="empty-state card">
            <FaBox style={{ fontSize: '48px', color: '#d1d5db', marginBottom: '16px' }} />
            <p>No orders found</p>
            {!isSeller && (
              <Link to="/products" className="btn btn-primary">
                Browse Products
              </Link>
            )}
          </div>
        ) : (
          <div className="orders-list">
            {filteredOrders.map(order => (
              <div key={order._id} className="order-card card">
                <div className="order-header">
                  <div className="order-info">
                    {getStatusIcon(order.status)}
                    <div>
                      <h3>Order #{order.orderNumber}</h3>
                      <p className="order-date">
                        {new Date(order.createdAt).toLocaleDateString('en-IN', {
                          day: 'numeric',
                          month: 'short',
                          year: 'numeric'
                        })}
                      </p>
                    </div>
                  </div>
                  <span className={`badge ${getStatusBadgeClass(order.status)}`}>
                    {order.status.replace('_', ' ').toUpperCase()}
                  </span>
                </div>

                <div className="order-body">
                  <div className="order-details">
                    <div className="detail-item">
                      <span className="label">{isSeller ? 'Buyer:' : 'Seller:'}</span>
                      <span className="value">
                        {isSeller 
                          ? order.buyer?.businessName || order.buyer?.phone
                          : order.seller?.businessName || 'Unknown'}
                      </span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Items:</span>
                      <span className="value">{order.items?.length || 0} item(s)</span>
                    </div>
                    <div className="detail-item">
                      <span className="label">Payment:</span>
                      <span className="value">
                        {order.payment?.method?.toUpperCase() || 'N/A'}
                        {order.payment?.status === 'completed' && (
                          <span style={{ 
                            marginLeft: '8px', 
                            color: '#10b981', 
                            fontSize: '12px',
                            fontWeight: '600'
                          }}>
                            ✓ PAID
                          </span>
                        )}
                      </span>
                    </div>
                  </div>

                  <div className="order-pricing">
                    <div className="pricing-row">
                      <span>Subtotal:</span>
                      <span>₹{order.pricing?.subtotal?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="pricing-row">
                      <span>GST:</span>
                      <span>₹{order.pricing?.gst?.toLocaleString('en-IN')}</span>
                    </div>
                    <div className="pricing-row total">
                      <span>Total:</span>
                      <span>₹{order.pricing?.total?.toLocaleString('en-IN')}</span>
                    </div>
                  </div>
                </div>

                <div className="order-footer">
                  <Link to={`/orders/${order._id}`} className="btn btn-outline">
                    View Details
                  </Link>
                  
                  {order.invoiceId && (
                    <Link to={`/invoices/${order.invoiceId}`} className="btn btn-invoice-link">
                      <FaFileInvoice /> Invoice
                    </Link>
                  )}
                  
                  {isSeller ? (
                    <div className="seller-actions">
                      {order.status === 'pending_payment' && (
                        <button 
                          className="btn btn-outline btn-sm"
                          onClick={() => handleCancelOrder(order._id)}
                        >
                          Cancel Order
                        </button>
                      )}
                      {order.status === 'paid' && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleStatusUpdate(order._id, 'confirmed')}
                        >
                          Confirm Order
                        </button>
                      )}
                      {order.status === 'confirmed' && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleStatusUpdate(order._id, 'processing')}
                        >
                          Start Processing
                        </button>
                      )}
                      {order.status === 'processing' && (
                        <button 
                          className="btn btn-primary"
                          onClick={() => handleStatusUpdate(order._id, 'shipped')}
                        >
                          Mark as Shipped
                        </button>
                      )}
                      {order.status === 'shipped' && (
                        <button 
                          className="btn btn-success"
                          onClick={() => handleMarkDelivered(order._id)}
                        >
                          Mark as Delivered
                        </button>
                      )}
                    </div>
                  ) : (
                    <div className="buyer-actions">
                      {(order.status === 'pending_payment' || order.payment?.status === 'pending') && (
                        <>
                          <button 
                            className="btn btn-primary"
                            onClick={() => handleCompletePayment(order._id)}
                          >
                            Complete Payment
                          </button>
                          <button 
                            className="btn btn-outline btn-danger"
                            onClick={() => handleCancelOrder(order._id)}
                          >
                            Cancel Order
                          </button>
                        </>
                      )}
                      {order.payment?.status === 'completed' && order.payment?.paidAt && (
                        <div className="payment-completed-info">
                          <FaCheckCircle style={{ color: '#10b981' }} />
                          <span>
                            Payment Completed on {new Date(order.payment.paidAt).toLocaleDateString('en-IN', {
                              day: 'numeric',
                              month: 'short',
                              year: 'numeric'
                            })}
                          </span>
                          {order.payment?.transactionId && (
                            <small style={{ display: 'block', marginTop: '4px', color: '#6b7280' }}>
                              Transaction ID: {order.payment.transactionId}
                            </small>
                          )}
                        </div>
                      )}
                      {order.status === 'shipped' && (
                        <button 
                          className="btn btn-success"
                          onClick={() => handleMarkDelivered(order._id)}
                        >
                          Confirm Delivery
                        </button>
                      )}
                    </div>
                  )}
                </div>

                {order.shipping?.trackingId && (
                  <div className="tracking-info">
                    <FaTruck /> Tracking ID: <strong>{order.shipping.trackingId}</strong>
                    <span className="tracking-courier">via {order.shipping.courier}</span>
                  </div>
                )}
              </div>
            ))}
          </div>
        )}
      </div>
    </Layout>
  );
};

export default Orders;
